import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import type { Draft } from 'immer';
import type { PlaybackState, PlaybackPosition, PracticeConfig, PracticeHand, Chord } from '@/types';
import { createAnchor, transportTimeAt, type PlaybackAnchor } from '@/utils/playbackClock';
import { getAudioNow } from '@/utils/audioNow';
import { installSeamStoreBridge } from '@/utils/testSeam';
import { lastChordIndexStrictlyBefore } from '@/utils/noteScheduler';

// Required once at module load: the store's playedNotes field is a Set,
// and Immer's default build has no Set/Map support without this call.
enableMapSet();

/**
 * OTO-027: already-resolved time bounds for a `skipBy` call. Resolved at
 * the component layer (which owns the score timeline) since the store
 * itself has no timeline access -- `config.loopStart`/`loopEnd` are MEASURE
 * NUMBERS, not seconds; converting them requires the timeline's measure
 * list, which only `PracticeView` / the engine hooks have.
 */
export interface SkipContext {
  totalDuration: number;
  /** Resolved loop-start time in SECONDS, or null when no loop is active. */
  loopStartTime: number | null;
  /** Resolved loop-end time in SECONDS, or null when no loop is active. */
  loopEndTime: number | null;
  /** Score chords, ascending by `startTime` -- used for wait-mode re-arm. */
  chords: Chord[];
}

/**
 * OTO-027: landing a skip exactly on `loopEndTime` would trigger the
 * engine's per-frame `applyLoopWrap` on the very next frame (it wraps
 * whenever `currentTime >= loopEndTime` while playing), immediately
 * snapping back to loop start -- the opposite of what an in-loop skip
 * button press should feel like. Clamping to `loopEndTime - LOOP_END_EPSILON`
 * instead avoids that collision.
 */
export const LOOP_END_EPSILON = 0.01;

/**
 * OTO-027: pure clamp for a `skipBy` target. Two clamp domains:
 * - `currentTime` already inside the active loop (`loopStartTime <=
 *   currentTime <= loopEndTime`, both non-null -- checked with `!== null`,
 *   never truthiness, since a loop legitimately starting at `0` must still
 *   count as active): clamp to `[loopStartTime, loopEndTime -
 *   LOOP_END_EPSILON]` (floored at `loopStartTime` for degenerate
 *   sub-epsilon loops).
 * - otherwise (no loop, or `currentTime` already outside the loop -- e.g.
 *   reached via the free-roaming seek-bar): clamp to `[0,
 *   ctx.totalDuration]`. This is the direction-inversion fix: an
 *   always-clamp-to-loop rule would make a forward skip from beyond
 *   `loopEndTime` jump BACKWARD into the loop.
 */
export function clampSkipTarget(target: number, currentTime: number, ctx: SkipContext): number {
  const { totalDuration, loopStartTime, loopEndTime } = ctx;
  // Half-open interval [loopStartTime, loopEndTime), matching the engine's own
  // wrap contract (usePlaybackEngine's applyLoopWrap fires at
  // `currentTime >= loopEndTime`, i.e. exactly loopEndTime is already "past" the
  // loop). A `<=` check here misclassified that boundary as inside the loop,
  // pulling a skip landing exactly there BACKWARD into a loop it had already
  // exited (review-verified regression, OTO-027).
  const insideLoop =
    loopStartTime !== null &&
    loopEndTime !== null &&
    currentTime >= loopStartTime &&
    currentTime < loopEndTime;

  if (insideLoop) {
    const effectiveEnd = Math.max(loopStartTime, loopEndTime - LOOP_END_EPSILON);
    return Math.min(effectiveEnd, Math.max(loopStartTime, target));
  }

  return Math.min(totalDuration, Math.max(0, target));
}

interface PlaybackStore {
  // Playback State
  state: PlaybackState;
  position: PlaybackPosition;

  /** Audio-clock anchor (OTO-22): non-null exactly while the transport is
   *  actively advancing (`state === 'playing'` and not frozen). Every
   *  lifecycle event that starts, stops, or discontinuously moves the
   *  transport re-derives it through the `reanchor` funnel below, in the
   *  same `set()` as its other transport writes -- see the anchor
   *  lifecycle table in the OTO-22 plan's Approach §1. */
  anchor: PlaybackAnchor | null;
  /** True while the transport is intentionally frozen (tab hidden) but
   *  `state` still reads `'playing'` -- distinguishes "paused" (anchor
   *  null, transport genuinely stopped) from "playing but not advancing
   *  right now" for the visibility lifecycle (T7). */
  transportFrozen: boolean;
  /** Bumped by `wrapToLoopStart` only -- the scheduler's wrap-specific
   *  resync signal, distinct from the `playedNotes` identity change a
   *  seek also produces (M1: a wrap needs a strict-before cursor at
   *  `loopStart`; a seek does not). */
  loopWrapEpoch: number;
  /** OTO-22 follow-up fix (manual e2e QA, 2026-07-16): the PURE loop-start
   *  measure-boundary time, set alongside `loopWrapEpoch` by
   *  `wrapToLoopStart`. `position.currentTime` after a wrap is the actual
   *  *landing* time (`loopStart + ((overshoot) % loopLength)` from
   *  `applyLoopWrap`, needed to keep multi-wrap stall-recovery correct) --
   *  never exactly `loopStart`, since the rAF frame that detects the
   *  crossing always samples strictly after it. Reconciling the scheduler
   *  cursor's strict-before check against that drifted landing time instead
   *  of this pure boundary made `landing > loopStart` always true, so the
   *  chord sitting exactly at `loopStart` was wrongly classified as
   *  "already played" and silently dropped on EVERY wrap pass (`useAutoPlay`
   *  must compare against this field, not `position.currentTime`, for M1). */
  loopWrapBoundaryTime: number | null;

  // Practice Configuration
  config: PracticeConfig;

  // Wait Mode State
  currentChord: Chord | null;
  playedNotes: Set<number>;
  isChordComplete: boolean;

  // Actions - Playback Control
  play: () => void;
  pause: () => void;
  stop: () => void;
  seek: (time: number) => void;
  /** OTO-027: transport-skip by a signed delta (seconds), clamped to the
   *  active loop's resolved time bounds when the current position is
   *  already inside it, else to `[0, ctx.totalDuration]` (see
   *  `clampSkipTarget`). Reads the current position via the same
   *  anchor-derived convention as `pause()`/`setTempoMultiplier()` --
   *  never the render-quantized subscribed value -- then delegates to
   *  `seek()` for the actual transport move (chord-state reset +
   *  unconditional re-anchor while playing). `ctx` is resolved by the
   *  caller (component layer) from the score timeline; the store itself
   *  has no timeline access. */
  skipBy: (deltaSeconds: number, ctx: SkipContext) => void;
  setPosition: (position: Partial<PlaybackPosition>) => void;
  /** Atomically wraps the transport back to a loop's start: rewinds
   *  position, resets `playedNotes` (silence + wait-mode reset, like a
   *  seek), bumps `loopWrapEpoch`, and re-anchors -- all in one `set()`
   *  so no subscriber ever observes a torn intermediate state.
   *  `landingTime` is the (possibly overshoot-drifted) transport position
   *  to resume from; `loopStartBoundary` is the pure measure-boundary time
   *  for cursor reconciliation (defaults to `landingTime` when omitted, the
   *  pre-fix behavior existing unit tests rely on). */
  wrapToLoopStart: (landingTime: number, loopStartBoundary?: number) => void;
  /** Tab hidden while playing (R1): freezes the transport at its current
   *  derived position and nulls the anchor, keeping `state === 'playing'`.
   *  A no-op when there is no active anchor to freeze. */
  freezeTransport: () => void;
  /** Tab visible again (R1): re-anchors at the frozen position. A no-op
   *  when not frozen, or frozen but no longer in the `'playing'` state. */
  thawTransport: () => void;

  // Actions - Practice Config
  setHand: (hand: PracticeHand) => void;
  setWaitMode: (enabled: boolean) => void;
  setTempoMultiplier: (multiplier: number) => void;
  setLoop: (start: number | null, end: number | null) => void;
  toggleSustain: () => void;
  toggleMetronome: () => void;
  setMetronomeVolume: (volume: number) => void;
  setShowNoteNames: (show: boolean) => void;

  // Actions - Wait Mode
  setCurrentChord: (chord: Chord | null) => void;
  addPlayedNote: (pitch: number) => void;
  removePlayedNote: (pitch: number) => void;
  clearPlayedNotes: () => void;
  checkChordComplete: (expectedNotes: number[]) => boolean;
  advanceToNextChord: () => void;
}

const defaultConfig: PracticeConfig = {
  hand: 'both',
  waitMode: false,
  tempoMultiplier: 1.0,
  loopStart: null,
  loopEnd: null,
  showNoteNames: true,
  showFingerNumbers: false,
  sustain: true,
  metronome: false,
  metronomeVolume: 0.5,
};

const defaultPosition: PlaybackPosition = {
  currentTime: 0,
  currentMeasure: 1,
  currentBeat: 1,
  nextChordIndex: 0,
};

/**
 * The store's single re-anchor funnel (OTO-22 Approach §1): every action
 * that starts or discontinuously moves the transport creates its new
 * anchor through here, inside its own `set()` call, never deferred to a
 * later frame. This is what closes the B2 double-fire/burst window -- a
 * scheduler tick between two store updates can never observe a new
 * position with a stale anchor.
 */
function reanchor(state: Draft<PlaybackStore>, transportTime: number): void {
  state.anchor = createAnchor({
    audioNow: getAudioNow(),
    transportTime,
    tempoMultiplier: state.config.tempoMultiplier,
  });
}

export const usePlaybackStore = create<PlaybackStore>()(
  immer((set, get) => ({
    state: 'stopped',
    position: { ...defaultPosition },
    anchor: null,
    transportFrozen: false,
    loopWrapEpoch: 0,
    loopWrapBoundaryTime: null,
    config: { ...defaultConfig },
    currentChord: null,
    playedNotes: new Set(),
    isChordComplete: false,

    // Playback Control
    play: () =>
      set((state) => {
        state.state = 'playing';
        // Second line of defense (OTO-22 council fix): a stale `transportFrozen`
        // from a prior freeze/pause/resume cycle must never survive into a
        // fresh play -- see pause()/stop() for the primary clear site.
        state.transportFrozen = false;
        reanchor(state, state.position.currentTime);
      }),

    pause: () =>
      set((state) => {
        if (state.anchor) {
          state.position.currentTime = transportTimeAt(state.anchor, getAudioNow());
        }
        state.state = 'paused';
        state.anchor = null;
        // Unconditionally clear: once genuinely paused, "frozen-while-playing"
        // is meaningless. Without this, a freeze -> pause (e.g. AudioContext
        // suspend while tab hidden) leaves transportFrozen stuck true forever,
        // since thawTransport() only clears it when state === 'playing'.
        state.transportFrozen = false;
      }),

    stop: () =>
      set((state) => {
        state.state = 'stopped';
        state.position = { ...defaultPosition };
        state.currentChord = null;
        state.playedNotes = new Set();
        state.isChordComplete = false;
        state.anchor = null;
        // See pause() above: unconditional clear, same rationale.
        state.transportFrozen = false;
      }),

    seek: (time) =>
      set((state) => {
        state.position.currentTime = time;
        // Reset chord index so auto-play re-syncs from new position
        state.position.nextChordIndex = 0;
        state.currentChord = null;
        state.playedNotes = new Set();
        state.isChordComplete = false;
        // Unconditional re-anchor while playing, for ANY seek magnitude (B4) --
        // no minimum-delta heuristic. While paused/stopped/waiting there is no
        // active anchor to update; it stays null (matches existing semantics).
        if (state.state === 'playing') {
          reanchor(state, time);
        }
      }),

    skipBy: (deltaSeconds, ctx) => {
      const state = get();
      const wasWaiting = state.state === 'waiting';
      const current = state.anchor
        ? transportTimeAt(state.anchor, getAudioNow())
        : state.position.currentTime;
      const target = clampSkipTarget(current + deltaSeconds, current, ctx);
      get().seek(target);

      if (wasWaiting) {
        // seek() leaves state === 'waiting' untouched (by design) but clears
        // currentChord to null -- re-arm the first chord at-or-after the new
        // position instead of leaving the UI stuck on "Waiting for notes...".
        // Strict-before mirrors the M1 loop-wrap cursor rule: a chord sitting
        // exactly at the landing time must be armed, not classified as
        // already played.
        const idx = lastChordIndexStrictlyBefore(ctx.chords, target) + 1;
        const candidate = ctx.chords[idx] ?? null;
        // Review-verified regression (OTO-027): a candidate at or past the
        // active loop's end will never actually play before the engine wraps
        // back to loopStart -- arming it here would show a chord the user can
        // never reach in this pass. Same half-open [loopStartTime,
        // loopEndTime) boundary as clampSkipTarget; falls back to the same
        // "nothing left to wait for" -> pause() path as running off the end
        // of the whole score.
        const targetInsideLoop =
          ctx.loopStartTime !== null &&
          ctx.loopEndTime !== null &&
          target >= ctx.loopStartTime &&
          target < ctx.loopEndTime;
        const nextChord =
          targetInsideLoop && candidate && candidate.startTime >= ctx.loopEndTime!
            ? null
            : candidate;
        if (nextChord) {
          get().setCurrentChord(nextChord);
        } else {
          get().pause();
        }
      }
    },

    setPosition: (position) =>
      set((state) => {
        Object.assign(state.position, position);
      }),

    wrapToLoopStart: (landingTime, loopStartBoundary) =>
      set((state) => {
        state.position.currentTime = landingTime;
        state.playedNotes = new Set();
        state.loopWrapEpoch += 1;
        state.loopWrapBoundaryTime = loopStartBoundary ?? landingTime;
        reanchor(state, landingTime);
      }),

    freezeTransport: () =>
      set((state) => {
        if (!state.anchor) return;
        state.position.currentTime = transportTimeAt(state.anchor, getAudioNow());
        state.anchor = null;
        state.transportFrozen = true;
      }),

    thawTransport: () =>
      set((state) => {
        if (!state.transportFrozen || state.state !== 'playing') return;
        state.transportFrozen = false;
        reanchor(state, state.position.currentTime);
      }),

    // Practice Config
    setHand: (hand) =>
      set((state) => {
        state.config.hand = hand;
      }),

    setWaitMode: (enabled) =>
      set((state) => {
        state.config.waitMode = enabled;
      }),

    setTempoMultiplier: (multiplier) =>
      set((state) => {
        // Read the derived position under the OLD anchor before replacing it,
        // so the new anchor continues from the correct instant, not audioTime=0.
        const derivedPosition = state.anchor
          ? transportTimeAt(state.anchor, getAudioNow())
          : state.position.currentTime;
        state.config.tempoMultiplier = Math.max(0.25, Math.min(2.0, multiplier));
        if (state.anchor) {
          reanchor(state, derivedPosition);
        }
      }),

    setLoop: (start, end) =>
      set((state) => {
        state.config.loopStart = start;
        state.config.loopEnd = end;
      }),

    toggleSustain: () =>
      set((state) => {
        state.config.sustain = !state.config.sustain;
      }),

    toggleMetronome: () =>
      set((state) => {
        state.config.metronome = !state.config.metronome;
      }),

    setMetronomeVolume: (volume) =>
      set((state) => {
        state.config.metronomeVolume = Math.max(0, Math.min(1, volume));
      }),

    setShowNoteNames: (show) =>
      set((state) => {
        state.config.showNoteNames = show;
      }),

    // Wait Mode
    setCurrentChord: (chord) =>
      set((state) => {
        state.currentChord = chord;
        state.playedNotes = new Set();
        state.isChordComplete = false;
        if (chord) {
          if (state.anchor) {
            state.position.currentTime = transportTimeAt(state.anchor, getAudioNow());
          }
          state.anchor = null;
          state.state = 'waiting';
        }
      }),

    addPlayedNote: (pitch) =>
      set((state) => {
        state.playedNotes.add(pitch);
      }),

    removePlayedNote: (pitch) =>
      set((state) => {
        state.playedNotes.delete(pitch);
      }),

    clearPlayedNotes: () =>
      set((state) => {
        state.playedNotes = new Set();
      }),

    checkChordComplete: (expectedNotes) => {
      const { playedNotes } = get();
      // Check if all expected notes are in played notes (superset allowed)
      const complete = expectedNotes.every((note) => playedNotes.has(note));
      set((state) => {
        state.isChordComplete = complete;
      });
      return complete;
    },

    advanceToNextChord: () =>
      set((state) => {
        state.position.nextChordIndex += 1;
        state.playedNotes = new Set();
        state.isChordComplete = false;
        state.state = 'playing';
        reanchor(state, state.position.currentTime);
      }),
  }))
);

// OTO-22 (T8): single call site wiring this store into the e2e test seam
// (`utils/testSeam.ts`) -- see that module's docstring for the DEV-gating
// and tree-shaking rationale.
installSeamStoreBridge(usePlaybackStore);
